import React, { useEffect, useState, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { useSelector, useDispatch } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Button, Card, CardBody } from "reactstrap";
import { useReactToPrint } from "react-to-print";

import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import ConfirmModal from "components/shared-components/ConfirmModal";
import { tableFormateDate } from "pages/DataFormatInvoice/utils";
import InvoiceTemplateModal from "./InvoiceTemplateModal";
import InvoiceTemplateViewer from "./InvoiceTemplateViewer";
import { InvoiceTemplateProvider } from "./InvoiceTemplateContext";
import { reducer, initialState } from "./InvoiceTemplateEditorReducer";

import { getListInvoiceTemplate, deleteInvoiceTemplate } from "store/actions";
import {
  NormalInvoiceTemplate,
  RootInvoiceTemplate,
  invoiceTypes,
  invoiceTemplateStatus,
} from "./const";

const initializeColumns = t => {
  return [
    {
      dataField: "khmhdon",
      text: t("list.table.columns.invoiceType"),
      formatter: (cell, row) => {
        return invoiceTypes.find(type => type.value === cell)?.label;
      },
    },
    {
      dataField: "khhdon",
      text: t("list.table.columns.serialNo"),
      sort: true,
    },
    {
      dataField: "config.invoiceTemplateRootCode",
      text: t("list.table.columns.rootInvoiceCode"),
      sort: true,
    },
    {
      dataField: "code",
      text: t("list.table.columns.invoiceCode"),
      sort: true,
    },
    {
      dataField: "auth_status",
      text: t("list.table.columns.status"),
      sort: true,
      formatter: (cell, row) => {
        if (cell === invoiceTemplateStatus.NEW) {
          return "Mới lập";
        }
        if (cell === invoiceTemplateStatus.USED) {
          return "Đang sử dụng";
        }
      },
    },
    {
      dataField: "maker_id",
      text: t("list.table.columns.createdBy"),
      formatter: cell => "_",
    },
    {
      dataField: "maker_date",
      text: t("list.table.columns.createdDate"),
      formatter: cell => tableFormateDate(cell),
    },
    {
      dataField: "checker_id",
      text: t("list.table.columns.updatedBy"),
      formatter: cell => "_",
    },
    {
      dataField: "checker_date",
      text: t("list.table.columns.updatedDate"),
      formatter: cell => tableFormateDate(cell),
    },
  ];
};

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const ListInvoiceTemplates = props => {
  const query = useQuery();
  const history = useHistory();
  const appDispatch = useDispatch();
  const contentPrint = useRef(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t, i18n } = useTranslation(["invoiceTemplate"]);

  const isRootTemplate = Boolean(query.get("root-template"));
  const [invoiceData, setInvoiceData] = useState(null);

  const [isShowConfirmDeleteModal, setIsShowConfirmDeleteModal] =
    useState(false);

  const [isShowViewInvoiceTemplateModal, setIsViewInvoiceTemplateModal] =
    useState(false);
  const [selectedIDToDelete, setSelectedIDToDelete] = useState(null);
  const { buStoreHeader } = useSelector(state => state.Dashboard);
  const invoiceTemplate = useSelector(state => state.invoiceTemplate);
  const {
    loadingList,
    errorList,
    list: invoiceTemplateList,
    delete: templateDelete,
  } = invoiceTemplate;

  const tableColumns = initializeColumns(t);

  const handleViewInvoiceTemplate = value => {
    setInvoiceData(value.config);
    setIsViewInvoiceTemplateModal(true);
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, columnFilter }
  ) => {
    invoiceTemplateList.params.sort = sortField
      ? sortOrder + sortField
      : invoiceTemplateList.params.sort;
    invoiceTemplateList.params.columnFilter = columnFilter || {};
    invoiceTemplateList.params.page = page || 1;
    invoiceTemplateList.params.sizePerPage = sizePerPage || 10;
    invoiceTemplateList.params.columnFilter = {
      ...invoiceTemplateList.params.columnFilter,
      "filter[record_status]": invoiceTemplateStatus.ACTIVE,
      "filter[type]": isRootTemplate
        ? RootInvoiceTemplate
        : NormalInvoiceTemplate,
    };
    appDispatch(getListInvoiceTemplate(invoiceTemplateList.params));
  };

  const onSearchAllHandle = value => {
    invoiceTemplateList.params.keywords = value;
    invoiceTemplateList.params.columnFilter = {
      ...invoiceTemplateList.params.columnFilter,
      "filter[record_status]": invoiceTemplateStatus.ACTIVE,
      "filter[type]": isRootTemplate
        ? RootInvoiceTemplate
        : NormalInvoiceTemplate,
    };
    appDispatch(getListInvoiceTemplate(invoiceTemplateList.params));
  };

  const listRowAction = [
    {
      icon: <i className="fas fas fa-eye fa-lg text-primary" />,
      text: t("list.message.viewInvoiceTemplate"),
      onClick: handleViewInvoiceTemplate
    },
    {
      icon: <i className="fas fa-pen fa-lg text-primary ms-2" />,
      text: t("list.message.editInvoiceTemplate"),
      onClick: event => {
        history.push(`/invoice-templates/${event.id}`);
      },
      conditionVisible: {
        auth_status: [invoiceTemplateStatus.NEW],
      },
    },
    {
      icon: <i className="fas fa-trash fa-lg text-danger ms-2" />,
      text: t("list.message.deleteInvoiceTemplate"),
      onClick: event => {
        setIsShowConfirmDeleteModal(true);
        setSelectedIDToDelete(event.id);
      },
      conditionVisible: {
        auth_status: [invoiceTemplateStatus.NEW],
      },
    },
  ];

  const getData = () => {
    invoiceTemplateList.params.columnFilter = {
      ...invoiceTemplateList.params.columnFilter,
      "filter[record_status]": "O",
      "filter[type]": isRootTemplate
        ? RootInvoiceTemplate
        : NormalInvoiceTemplate,
    };

    appDispatch(getListInvoiceTemplate(invoiceTemplateList.params));
  };

  useEffect(() => {
    if (
      templateDelete &&
      templateDelete.success === true &&
      invoiceTemplateList
    ) {
      appDispatch(getListInvoiceTemplate(invoiceTemplateList.params));
    }
  }, [appDispatch, templateDelete]);

  useEffect(() => {
    if (buStoreHeader) {
      getData();
    }
  }, [appDispatch, buStoreHeader]);

  const handlePrint = useReactToPrint({
    content: () => contentPrint.current,
  });

  return (
    <div className="page-content">
      <MetaTags>
        <title>{t("list.title.pageTitle")}</title>
      </MetaTags>

      {/* View Invoice Template Modal */}
      <InvoiceTemplateModal
        isOpen={isShowViewInvoiceTemplateModal}
        toggle={() => {
          setIsViewInvoiceTemplateModal(!isShowViewInvoiceTemplateModal);
          setSelectedIDToDelete(null);
        }}
        template={
          <InvoiceTemplateProvider value={{ state, dispatch }}>
            <div ref={contentPrint}>
              <InvoiceTemplateViewer data={invoiceData} />
            </div>
          </InvoiceTemplateProvider>
        }
        actions={
          <div className="d-flex">
            <Button
              className="d-flex align-items-center mx-1"
              color="primary"
              outline
            >
              <i className="bx bx-show bx-xs" />
              Xem PDF
            </Button>
            <Button
              className="d-flex align-items-center mx-1"
              color="primary"
              outline
              onClick={handlePrint}
            >
              <i className="bx bx-printer bx-xs" />
              In hóa đơn mẫu
            </Button>
            <Button
              className="d-flex align-items-center mx-1"
              color="primary"
              outline
            >
              <i className="bx bx-file bx-xs" />
              Mẫu dùng thử
            </Button>
            <Button
              className="d-flex align-items-center mx-1"
              color="primary"
              outline
            >
              <i className="bx bx-file bx-xs" />
              Mẫu chuyển đổi
            </Button>
          </div>
        }
      />

      {/* Delete User Modal */}
      <ConfirmModal
        isOpen={isShowConfirmDeleteModal}
        titleHeader={"Xác nhận xóa nội dung này"}
        titleBody={"Nội dung này sẽ bị Xóa. Vui lòng xác nhận thao tác"}
        textBtnConfirm={"Xác nhận"}
        textBtnCancel={"Hủy bỏ"}
        onConfirm={() => {
          appDispatch(deleteInvoiceTemplate(selectedIDToDelete));
          setSelectedIDToDelete(null);
          setIsShowConfirmDeleteModal(false);
        }}
        toggle={() => {
          setIsShowConfirmDeleteModal(!isShowConfirmDeleteModal);
          setSelectedIDToDelete(null);
        }}
      />

      <div className="container-fluid">
        <BreadcrumbWithLink
          title={t("list.title.pageTitle")}
          breadcrumbItems={[
            { name: t("common:category") },
            { name: t("list.title.pageTitle") },
          ]}
        />
        <Card>
          <CardBody>
            <RemoteDataTable
              // onRowsSelect={onRowsSelect}
              keyField="id"
              data={invoiceTemplateList.items}
              columns={tableColumns}
              page={invoiceTemplateList.params.page}
              sizePerPage={invoiceTemplateList.params.sizePerPage}
              totalSize={invoiceTemplateList.totalCount}
              onTableChange={handleTableChange}
              onSearchAllHandle={onSearchAllHandle}
              // optionShowColumnHandel={optionShowColumnHandler}
              // rowEvents={rowEventsHandler}
              listRowAction={listRowAction}
              loading={loadingList}
            >
              <Link
                to="/invoice-templates/create/select-template"
                className="btn btn-primary btn-label"
              >
                <i className="fas fa-plus label-icon" />
                {t("list.title.create")}
              </Link>
            </RemoteDataTable>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ListInvoiceTemplates;
